import React, { useEffect, useMemo, useCallback, useRef, useState } from "react";
import {
  Table, TableBody, TableCell, TableHead, TableRow,
  IconButton, styled, Box, Typography,
} from "@mui/material";
import {
  borderSize, colors, fontSize, fontWeight, gaps, margin, padding,
} from "theme/defaultStyle";
import ComboBoxField from "controls/global/combo-box-field";
import DeleteIcon from "@mui/icons-material/Delete";
import TextInputField from "controls/global/text-input-field";
import CurrencyInputField from "controls/global/currency-field";
import { useLookup } from "utils/context/LookupContext";
import {
  usePartyBuyerBorrowerNames, usePartyBuyerHasValidBuyer,
} from "utils/context/PartyBuyerBorrowerContext";
import { ProductType } from "utils/data/enum";
import {
  usePartyLenderHasValidLender, usePartyLenderNames,
} from "utils/context/PartyLenderContext";
import useFormWrapper from "utils/custom-hooks/useFormWrapper";
import { useFieldArray } from "react-hook-form";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { v4 as uuidv4 } from "uuid";
import LightTooltip from "controls/global/light-tooltip";
import { UWAPolicyInfo } from "entities/UIModel";
import { hasNotNullOrEmptyProp } from "pages/file/utils/helper";

const Container = styled(Box)({
  width: "100%",
  padding: padding.xlarge1,
  border: `${borderSize.xsmall} solid ${colors.grey09}`,
  boxSizing: "border-box",
});

const StyledTable = styled(Table)({
  borderBottom: `${borderSize.xsmall} solid ${colors.grey17}`,
  width: "100%",
  borderTop: "none",
  tableLayout: "auto",
});

const StyledTableRow = styled(TableRow)({
  fontFamily: "Inter",
  "& .MuiTableCell-root": {
    padding: padding.zero,
    fontSize: fontSize.medium,
    verticalAlign: "middle",
    borderBottom: `${borderSize.xsmall} solid ${colors.grey04}`,
    borderLeft: "none",
    borderRight: "none",
    fontFamily: "Inter",
    fontWeight: "400",
  },
  "& .MuiTableCell-root:first-of-type": { borderLeft: `${borderSize.xsmall} solid ${colors.grey04}` },
  "& .MuiTableCell-root:last-of-type": { borderRight: `${borderSize.xsmall} solid ${colors.grey04}` },
  "& input": { padding: `${padding.xsmall} ${padding.small1} !important`, fontSize: fontSize.medium },
  "& .MuiOutlinedInput-root": {
    height: "40px !important",
    border: "none",
    "& .MuiOutlinedInput-notchedOutline": { border: "none" },
  },
  "& .MuiInputBase-input::placeholder": { color: "#666", opacity: 1 },
});

const StyledTypography = styled(Typography)({
  color: colors.blue01, fontSize: fontSize.xlarge, fontWeight: fontWeight.bold1,
});

const HeaderTableCell = styled(TableCell)({
  fontSize: fontSize.medium, 
  textTransform: "uppercase", 
  fontFamily: "Inter", 
  padding: `${padding.zero} ${padding.zero} ${padding.zero} ${padding.small1}`,
  fontWeight: fontWeight.bold1,
});

const StyledHeader = styled(Box)({
  display: "flex", alignItems: "center", marginBottom: margin.xlarge1, gap: `${gaps.small1}`,
});

const SmallTableCell = styled(TableCell, {
  shouldForwardProp: (p) => p !== "$allDisabled",
})<{ $disabled?: boolean; $allDisabled?: boolean }>(({ $allDisabled }) => ({
  width: 40,
  padding: 0,
  backgroundColor: $allDisabled ? colors.grey28 : "transparent",
}))
const PolicyTypeCell = styled(TableCell)({ width: "20%" });
const PolicyFormCell = styled(TableCell)({ width: "20%" });
const ProposedInsuredCell = styled(TableCell)({ width: "21%"});
const EndorsementsCell = styled(TableCell)({ width: "21%"});
const LiabilityAmountCell = styled(TableCell)({ width: "18%" });

const SpecialRisksError = styled(Box)({
  color: colors.red01, fontSize: fontSize.medium, fontWeight: fontWeight.normal1, lineHeight: `normal`,
});

const TooltipAnchor = styled("span")({
  display: "inline-block",
  width: "100%",
  pointerEvents: "auto",
});

type EllipsisTooltipProps = {
  title?: string;
  targetSelector?: string;
  disabled?: boolean;
  children: React.ReactNode;
  placement?: "top" | "bottom" | "left" | "right";
};

const EllipsisTooltip: React.FC<EllipsisTooltipProps> = ({
  title,
  targetSelector = 'input,textarea,[contenteditable="true"],.MuiInputBase-input',
  disabled,
  children,
  placement = "top",
}) => {
  const rootRef = useRef<HTMLSpanElement | null>(null);
  const [tooltipText, setTooltipText] = useState("");

  const compute = useCallback(() => {
    const root = rootRef.current;
    if (!root) return { over: false, text: "" };
    const el = (root.querySelector(targetSelector) as HTMLElement | null) || root;

    let text = title ?? "";
    if (!text) {
      const asInput = el as HTMLInputElement | HTMLTextAreaElement;
      text = (asInput?.value ?? el.textContent ?? "").trim();
    }
    if (!text) return { over: false, text: "" };

    const over =
      el.scrollWidth > el.clientWidth + 1 ||
      el.scrollHeight > el.clientHeight + 1;

    return { over, text };
  }, [title, targetSelector]);

  const handleMouseEnter = () => {
    const { over, text } = compute();
    setTooltipText(over ? text : "");
  };

  const handleMouseLeave = () => {
    setTooltipText("");
  };

  return (
    <LightTooltip
      title={tooltipText}
      arrow
      placement={placement}
    >
      <TooltipAnchor
        ref={rootRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {children}
      </TooltipAnchor>
    </LightTooltip>
  );
};

type PolicyRow = {
  integrationKey: string;  
  id: string;
  uwaPolicyTypeCode?: string | { text?: string; value?: string };
  policyForm?: string;
  proposedInsured?: string;
  endorsements?: string;
  policyAmount?: string | number;
};

interface Props {
  schema: string;
  disabled?: boolean;
  onChange?: () => void;
  existingUwaPolicyInfos?: Array<UWAPolicyInfo>;
  submitted: boolean;
  revising: boolean;
}

const blankRow = (): PolicyRow => ({
  integrationKey: uuidv4(),
  id: uuidv4(),
  uwaPolicyTypeCode: "",
  proposedInsured: "",
  endorsements: "",
  policyAmount: "",
});


const ProposedPoliciesGrid = ({
  schema, disabled, onChange, existingUwaPolicyInfos, submitted, revising,
}: Props) => {
  const [{ policyTypes }, { getPolicyTypes }] = useLookup();

  const {
    control,
    setValue,
    getValues,
    formState: { errors },
    trigger,
    clearErrors,
  } = useFormWrapper();

  const { fields, append, remove, replace } = useFieldArray({
    name: schema,
    control,
    keyName: "rhfId",
  });

  const [buyerBorrowerNames] = usePartyBuyerBorrowerNames(ProductType.Jacket);
  const [buyerBorrowerHasValidBuyer] = usePartyBuyerHasValidBuyer(ProductType.Jacket);
  const [lenderNames] = usePartyLenderNames();
  const [lenderHasValidLender] = usePartyLenderHasValidLender();

  const productIndex = Number(schema.split(".")[1] ?? 0);
  const productErrors = (errors as any)?.uwaProducts?.[productIndex] as any | undefined;
  type RHFFieldError = { message?: string; [k: string]: any };
  const uwaPolicyInfosErrors = productErrors?.uwaPolicyInfos as RHFFieldError[] | RHFFieldError | undefined;
  const arrayLevelMessage = (uwaPolicyInfosErrors as RHFFieldError | undefined)?.message as string | undefined;

  let fieldLevelMessage: string | undefined;
  if (Array.isArray(uwaPolicyInfosErrors)) {
    for (const row of uwaPolicyInfosErrors) {
      const msg = row?.uwaPolicyTypeCode?.message || row?.policyAmount?.message;
      if (msg) { fieldLevelMessage = msg as string; break; }
    }
  }
  const proposedPolicyErrorMessage = arrayLevelMessage || fieldLevelMessage;

  const getPolicyTypeText = useCallback((v: PolicyRow["uwaPolicyTypeCode"]) => {
    if (typeof v === "object" && v !== null) return (v as any).text ?? (v as any).value ?? "";
    return (v as string) ?? "";
  }, []);

  const isRowEmpty = useCallback((idx: number) => {
    const row = getValues(`${schema}.${idx}`) as PolicyRow | undefined;
    if (!row) return true;
    const pt = getPolicyTypeText(row.uwaPolicyTypeCode);
    const hasAmount = !!row.policyAmount && Number(row.policyAmount) > 0;
    return !pt && !(row.proposedInsured || "").trim() && !(row.endorsements || "").trim() && !hasAmount;
  }, [getValues, schema, getPolicyTypeText]);

  const appendBlankIfNeeded = useCallback(() => {
    const rows = (getValues(schema) as PolicyRow[] | undefined) ?? [];
    if (rows.length === 0) return;
    const lastIdx = rows.length - 1;
    if (!isRowEmpty(lastIdx)) {
      append(blankRow(), { shouldFocus: false });
    }
  }, [append, getValues, schema, isRowEmpty]);

  const validateAndClearArrayIfValid = useCallback(async (name: string) => {
    const ok = await trigger(name);
    if (ok) clearErrors(schema);
  }, [trigger, clearErrors, schema]);



  const policiesArray = useMemo(() => {
    const src = policyTypes as any;
    if (Array.isArray(src)) return src;
    if (src && typeof src === "object") {
      if (Array.isArray(src.items)) return src.items;
      return Object.values(src);
    }
    return [];
  }, [policyTypes]);

  const policyTypeOptions = useMemo(
    () =>
      policiesArray.map((policy: any) => {
        const clean = (v: any) =>
          typeof v === "string" ? v.replace(/^"+|"+$/g, "").replace(/^'+|'+$/g, "") : v;
        return {
          text: clean(policy.uwaPolicyTypeName) ?? "",
          value: clean(policy.uwaPolicyTypeCode) ?? "",
        };
      }),
    [policiesArray]
  );




  const handlePolicyTypeSelect = useCallback(
    (rowIndex: number, selectedValue: any) => {
      const code = selectedValue?.value ?? selectedValue;
      const policyText = selectedValue?.value ?? selectedValue?.text ?? selectedValue ?? "";

      let proposedInsuredValue = "";
      if (policyText === "LOAN" && lenderHasValidLender) {
        proposedInsuredValue = lenderNames;
      } else if (policyText === "OWNERS" && buyerBorrowerHasValidBuyer) {
        proposedInsuredValue = buyerBorrowerNames;
      }

      setValue(`${schema}.${rowIndex}.uwaPolicyTypeCode`, code, { shouldDirty: true });
      if (proposedInsuredValue) {
        setValue(`${schema}.${rowIndex}.proposedInsured`, proposedInsuredValue, { shouldDirty: true });
      }

      validateAndClearArrayIfValid(`${schema}.${rowIndex}.uwaPolicyTypeCode`);
      if (proposedInsuredValue) validateAndClearArrayIfValid(`${schema}.${rowIndex}.proposedInsured`);

      appendBlankIfNeeded();
      onChange?.();
    },
    [
      lenderHasValidLender, lenderNames, buyerBorrowerHasValidBuyer, buyerBorrowerNames,
      setValue, schema, validateAndClearArrayIfValid, appendBlankIfNeeded, onChange,
    ]
  );

  const handleTextChange = useCallback(
    (rowIndex: number, field: keyof PolicyRow, value: string | number) => {
      setValue(`${schema}.${rowIndex}.${field}`, value, { shouldDirty: true });
      appendBlankIfNeeded();
      onChange?.();
    },
    [setValue, schema, appendBlankIfNeeded, onChange]
  );

  const handleBlurValidate = useCallback(
    (rowIndex: number, field: keyof PolicyRow) => {
      validateAndClearArrayIfValid(`${schema}.${rowIndex}.${field}`);
    },
    [schema, validateAndClearArrayIfValid]
  );

  const handleRemoveAtIndex = useCallback(
    (rowIndex: number) => {
      const total = (getValues(schema) as PolicyRow[] | undefined)?.length ?? 0;
      if (total <= 1) return;
      if (disabled) return;
      if (isRowEmpty(rowIndex)) return;
      remove(rowIndex);
      clearErrors(schema);
    },
    [remove, getValues, schema, isRowEmpty, disabled, clearErrors]
  );

  const isValidPolicyRow = (policyRow: PolicyRow): boolean =>
    hasNotNullOrEmptyProp(policyRow, ["uwaPolicyTypeCode", "proposedInsured", "endorsements", "policyAmount"]);


  //#region useEffect section 
  useEffect(() => {
    getPolicyTypes();
  }, [getPolicyTypes]);

  useEffect(() => {
    const current = (getValues(schema) as PolicyRow[] | undefined) ?? [];
    // if ((existingUwaPolicyInfos?.length || 0) > 0 && current.length === 0) {
    //   const seeded = existingUwaPolicyInfos!.map<PolicyRow>((p) => ({
    //     id: p.id || uuidv4(),
    //     uwaPolicyTypeCode: p.uwaPolicyTypeCode ?? "",
    //     proposedInsured: p.proposedInsured ?? "",
    //     endorsements: p.endorsements ?? "",
    //     policyAmount: p.policyAmount ?? "",
    //   }));

    //   const last = seeded[seeded.length - 1];
    //   const lastEmpty = last
    //     ? !getPolicyTypeText(last.uwaPolicyTypeCode) &&
    //     !(last.proposedInsured || "").trim() &&
    //     !(last.endorsements || "").trim() &&
    //     !(Number(last.policyAmount) > 0)
    //     : true;


    //   console.log('replace-1');        
    //   replace(lastEmpty ? (seeded.length ? seeded : [blankRow()]) : [...seeded, blankRow()]);
    //   return;
    // }

    if (current.length === 0) {
      // console.log('replace-2');
      replace([blankRow()]);
    }
  }, [existingUwaPolicyInfos, submitted, revising]);

  useEffect(() => {
    // console.log("submitted:", submitted, "revising:", revising);
    // if (!submitted && !disabled) return;
    if (!submitted || (submitted && revising)) return;
    const rows = (getValues(schema) as PolicyRow[] | undefined) ?? [];
    // console.log("rows:", JSON.parse(JSON.stringify(rows)));
    // remove empty rows 
    const validRows = rows?.filter((r) => isValidPolicyRow(r));
    // console.log("validRows:", JSON.parse(JSON.stringify(validRows)));

    replace(validRows);
    // if (rows.length === 0) return;
    // let end = rows.length;
    // while (end > 1) {
    //   const r = rows[end - 1];
    //   const pt = getPolicyTypeText(r?.uwaPolicyTypeCode);
    //   const hasAmount = !!r?.policyAmount && Number(r?.policyAmount) > 0;
    //   const empty = !pt && !(r?.proposedInsured || "").trim() && !(r?.endorsements || "").trim() && !hasAmount;
    //   if (!empty) break;
    //   end -= 1;
    // }
    // if (end !== rows.length) { 
    //   console.log('replace-3');
    //   replace(rows.slice(0, end));
    // }
  }, [submitted, revising]); // disabled]);

  //#endregion

  return (
    <Container>
      <StyledHeader>
        <StyledTypography>Proposed Policies</StyledTypography>
        {proposedPolicyErrorMessage && (
          <>
            <ErrorOutlineIcon sx={{ color: colors.red01 }} fontSize="large" />
            <SpecialRisksError>{proposedPolicyErrorMessage}</SpecialRisksError>
          </>
        )}
      </StyledHeader>

      <StyledTable>
        <TableHead>
          <TableRow>
            <HeaderTableCell>Policy Type</HeaderTableCell>
            <HeaderTableCell>Policy Form</HeaderTableCell>
            <HeaderTableCell>Proposed Insured</HeaderTableCell>
            <HeaderTableCell>Endorsements</HeaderTableCell>
            <HeaderTableCell>Liability Amount</HeaderTableCell>
            <TableCell />
          </TableRow>
        </TableHead>

        <TableBody>
          {fields.map((item, index) => {
            const base = `${schema}.${index}`;
            const canDelete = !disabled && !isRowEmpty(index) && fields.length > 1;

            return (
              <StyledTableRow key={item.rhfId}>
                <PolicyTypeCell>
                  <ComboBoxField
                    placeholder="Policy Type*"
                    label=""
                    options={policyTypeOptions}
                    size="small"
                    noBorder
                    onChange={(_: any, selectedValue: any) => handlePolicyTypeSelect(index, selectedValue)}
                    onBlur={() => validateAndClearArrayIfValid(`${base}.uwaPolicyTypeCode`)}
                    paperStyle={{ width: 300 }}
                    controlTooltipOnMouseEvents
                    name={`${base}.uwaPolicyTypeCode`}
                    disabled={disabled}
                    showOutsideError={false}
                  />
                </PolicyTypeCell>

                <PolicyFormCell>
                  <EllipsisTooltip targetSelector="input,.MuiInputBase-input">
                    <TextInputField
                      size="small"
                      placeholder="Policy Form"
                      inputProps={{ maxLength: 300 }}
                      onChange={(e) => handleTextChange(index, "policyForm", e.target.value)}
                      onBlur={() => handleBlurValidate(index, "policyForm")}
                      name={`${base}.policyForm`}
                      disabled={disabled}
                    />
                  </EllipsisTooltip>
                </PolicyFormCell>                

                <ProposedInsuredCell>
                  <EllipsisTooltip targetSelector="input,.MuiInputBase-input">
                    <TextInputField
                      size="small"
                      placeholder="Proposed Insured"
                      inputProps={{ maxLength: 500 }}
                      onChange={(e) => handleTextChange(index, "proposedInsured", e.target.value)}
                      onBlur={() => handleBlurValidate(index, "proposedInsured")}
                      name={`${base}.proposedInsured`}
                      disabled={disabled}
                    />
                  </EllipsisTooltip>
                </ProposedInsuredCell>

                <EndorsementsCell>
                  <EllipsisTooltip targetSelector="input,.MuiInputBase-input">
                    <TextInputField
                      size="small"
                      placeholder="Endorsements"
                      inputProps={{ maxLength: 500 }}
                      onChange={(e) => handleTextChange(index, "endorsements", e.target.value)}
                      onBlur={() => handleBlurValidate(index, "endorsements")}
                      name={`${base}.endorsements`}
                      disabled={disabled}
                    />
                  </EllipsisTooltip>
                </EndorsementsCell>

                <LiabilityAmountCell>
                  <EllipsisTooltip targetSelector="input,.MuiInputBase-input">
                    <CurrencyInputField
                      placeholder="Liability Amount*"
                      onChange={(e: any) => handleTextChange(index, "policyAmount", e.target.value)}
                      onBlur={() => handleBlurValidate(index, "policyAmount")}
                      name={`${base}.policyAmount`}
                      disabled={disabled}
                      allowNegative={false}
                      showOutsideError={false}
                      showZero={true}
                    />
                  </EllipsisTooltip>
                </LiabilityAmountCell>

                <SmallTableCell align="center" $allDisabled={disabled}>
                  <IconButton
                    aria-label="delete-proposed-policy"
                    onClick={() => handleRemoveAtIndex(index)}
                    disabled={!canDelete}
                    sx={{ color: colors.blue01, "&.Mui-disabled": { color: colors.blue12 } }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </SmallTableCell>
              </StyledTableRow>
            );
          })}
        </TableBody>
      </StyledTable>
    </Container>
  );
};

export default ProposedPoliciesGrid;
